import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { LeadsModel } from '../models/lead/lead.model';
import { BaseBackendService } from './base-backend.service';
import { Observable, Subject, catchError, map, takeUntil } from 'rxjs';
import { SsoAuthService } from './sso.service';
import { IMAGE_SUMMARIZE_ENDPOINT } from 'app/app.constants';
import { User } from 'app/user-account/user.model';

@Injectable({
    providedIn: 'root', // This makes the LeadService globally available throughout the app
})
export class LeadService extends BaseBackendService<LeadsModel> {
    user: User;
    ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        http: HttpClient,
        protected override authService: AuthService,
        ssoService: SsoAuthService
    ) {
        super(http, authService, LeadsModel, 'lead', ssoService);

        this.authService.user$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
            if (res) {
                this.user = res;
            }
        });
    }

    // Structure Form Data to pass for summarization
    private getImageFormData(imageUrl: string): any {
        const buildImageSummarizeData = {
            'image-url': imageUrl,
        };
        return buildImageSummarizeData;
    }

    // POST request when uploading image URL to get summarization of the image
    summarizeImageFromURL(imageUrl: string): Observable<any> {
        const imageSummarizeURLBody = this.getImageFormData(imageUrl);
        return this.post(imageSummarizeURLBody, IMAGE_SUMMARIZE_ENDPOINT).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                return this.handleError(error);
            })
        );
    }

    // POST request when uploading image (FormData) to get summarization of the image
    summarizeImageFromFile(file: File): Observable<any> {
        const summarizeFormData = new FormData();
        summarizeFormData.append('image', file);

        return this.post(summarizeFormData, IMAGE_SUMMARIZE_ENDPOINT, { observeResponse: false }, false).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                // if 401 HTTP error, then call this function once again and stop recursion
                return this.handleError(error);
            })
        );
    }

    // POST request for new Lead creation
    createLead(passedLeadBody): Observable<any> {
        return this.post(passedLeadBody).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                return this.handleError(error);
            })
        );
    }

    // GET request to get all leads for a specific DBC USER
    getUserLeads(fetchOrgContacts:boolean = false): Observable<any> {
        // If logged in, proceed with fetching organization and other details
        const orgId = this.authService.getCurrentOrgId();
        const isAdmin = this.user.isAdmin(orgId);
        let QUERY_PARAM = `?organization=${orgId}&ordering=-created`;
        // If the user is not an admin, add leadOwnerId to the query
        if (!isAdmin || !fetchOrgContacts) {
            const leadOwnerId = this.authService.getUser()?.id;
            if(!leadOwnerId) {
                throw new Error('User ID not found');
            }
            QUERY_PARAM += `&lead_owner__is=${leadOwnerId}`;
        }
        // After determining all the parameters, call getData with the updated QUERY_PARAM
        return this.getData('', QUERY_PARAM).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                return this.handleError(error);
            })
        );
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
    }
}
